/**
 * El back office en su propio dominio · 02-11-2026
 *
 * Hasta esta fecha el panel de los clubes y el back office eran la misma web en
 * `app.kiapclubs.com`, y quien tiene las dos cosas —personal de Kiap con un club
 * propio, con una cuenta para cada una— se liaba: la misma dirección, la misma
 * pestaña, y entrar con una cuenta echaba a la otra.
 *
 * Ahora es **la misma construcción servida en dos dominios** (el mismo proyecto de
 * Cloudflare Pages), y cada uno enseña solo lo suyo:
 *
 * - `app.kiapclubs.com` → el panel de los clubes.
 * - `backoffice.kiapclubs.com` → el back office.
 *
 * La API le da a cada dominio **su propia cookie de refresco** (`BACKOFFICE_URL` en
 * el backend), así que las dos sesiones conviven, cada una con su cuenta.
 *
 * Se decide por el dominio en que se abre la página, no por la cuenta: la cuenta ya
 * la mira la puerta (`can_open`). Una ruta del otro lado lleva a la misma ruta en
 * su dominio, para que los marcadores y los enlaces viejos sigan valiendo.
 *
 * **Sin las dos variables —en local— todo sigue junto**, como antes: `:5173` sirve
 * las dos aplicaciones y la puerta reparte con `can_open`.
 */

export type Aplicacion = 'panel' | 'backoffice';

const origenDe = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

/**
 * El reparto, a partir de las dos direcciones. Una función y no constantes sueltas
 * para poder probarlo con cualquier pareja sin tocar las variables del entorno.
 */
export function repartoDeDominios(appUrl?: string, backofficeUrl?: string) {
  const app = origenDe(appUrl);
  const backoffice = origenDe(backofficeUrl);
  const separados = app !== null && backoffice !== null;
  const origen = (a: Aplicacion) => (a === 'backoffice' ? backoffice : app);
  const aplicacionDe = (origenActual: string): Aplicacion | null => {
    if (!separados) return null;
    return origenActual === backoffice ? 'backoffice' : 'panel';
  };

  return {
    /** ¿Cada aplicación en su dominio? */
    separados,

    /**
     * Qué aplicación sirve ese origen, o `null` si sirve las dos.
     *
     * Todo lo que no es el back office es el panel: también la vista previa de
     * Cloudflare (`*.pages.dev`), donde de todas formas la sesión no sobrevive a
     * una recarga (la cookie es de `kiapclubs.com`).
     */
    aplicacionDe,

    /** El dominio, sin esquema: lo que se le enseña a una persona. */
    dominioDe: (a: Aplicacion): string => {
      const o = origen(a);
      return o ? new URL(o).host : '';
    },

    /** La misma ruta, en el dominio de esa aplicación. */
    enSuDominio: (a: Aplicacion, ruta: string): string =>
      `${origen(a) ?? ''}${ruta}`,

    /**
     * Si esta ruta es de la otra aplicación, cuál; si es de aquí o de las dos,
     * `null`. Lo pregunta `main.tsx` **antes de montar nada** (ver `ruta…`).
     */
    aplicacionDeLaRuta: (
      origenActual: string,
      ruta: string
    ): Aplicacion | null => {
      const aqui = aplicacionDe(origenActual);
      if (aqui === null) return null;
      const limpia = ruta.length > 1 ? ruta.replace(/\/+$/, '') : ruta;
      const delBackoffice = limpia === '/admin' || limpia.startsWith('/admin/');
      if (aqui === 'panel' && delBackoffice) return 'backoffice';
      if (aqui === 'backoffice' && !delBackoffice && !DE_LAS_DOS.has(limpia)) {
        return 'panel';
      }
      return null;
    },
  };
}

/**
 * Las rutas que sirven las dos direcciones: la puerta, las que abren los correos
 * y la cuenta propia. Un enlace mágico pedido desde el back office se canjea en el
 * back office (`BACKOFFICE_URL` en el backend), y su `/acceso` tiene que estar ahí.
 */
const DE_LAS_DOS = new Set([
  '/',
  '/acceso',
  '/invitacion',
  '/restablecer',
  '/verificar-correo',
  '/mi-cuenta',
]);

const configurado = repartoDeDominios(
  import.meta.env.VITE_APP_URL as string | undefined,
  import.meta.env.VITE_BACKOFFICE_URL as string | undefined
);

export const dominiosSeparados = configurado.separados;

/** Qué aplicación sirve la página abierta, o `null` si sirve las dos (en local). */
export const aplicacionDeEsteDominio = (
  origen: string = window.location.origin
): Aplicacion | null => configurado.aplicacionDe(origen);

export const dominioDe = configurado.dominioDe;
export const enSuDominio = configurado.enSuDominio;

/**
 * La dirección a la que hay que irse **antes de arrancar**, o `null` para quedarse.
 *
 * Tiene que ser antes de montar React, y no en el guardián de cada ruta, por la
 * rotación del token de refresco: el proveedor de sesión arranca renovándolo, y si
 * la página salta al otro dominio con esa renovación en vuelo, el servidor ya ha
 * rotado el token pero el navegador no llega a guardar el nuevo. La siguiente
 * renovación usa el viejo, salta la detección de reutilización y **corta la
 * sesión**. Pasó en la prueba con navegador del 02-11-2026: abrir
 * `app.…/admin/clubes` con sesión en los dos sitios dejaba fuera al panel.
 */
export function rutaEnOtroDominio(
  ubicacion: Pick<
    Location,
    'origin' | 'pathname' | 'search' | 'hash'
  > = window.location
): string | null {
  const suya = configurado.aplicacionDeLaRuta(
    ubicacion.origin,
    ubicacion.pathname
  );
  if (suya === null) return null;
  return configurado.enSuDominio(
    suya,
    `${ubicacion.pathname}${ubicacion.search}${ubicacion.hash}`
  );
}
